<template>
  <nav
    class="flex flex-col flex-1 mt-5 overflow-y-auto divide-y divide-teal-800"
    aria-label="Sidebar"
  >
    <div class="px-2 space-y-1">
      <menu-item
        :to="{ name: 'Home' }"
        icon="HomeIcon"
        label="Home"
        :hide-label="hideLabel"
      />

      <menu-item
        v-if="$can('list', 'Reports')"
        :to="{ name: 'ReportList' }"
        icon="DocumentReportIcon"
        label="Reports"
        :hide-label="hideLabel"
      />

      <menu-item
        v-if="$can('list', 'Recording')"
        :to="{ name: 'RecordingList' }"
        icon="MusicNoteIcon"
        label="Recordings"
        :hide-label="hideLabel"
      />
      <menu-item
        v-if="hasQualityAccess"
        :to="qualityRoute"
        icon="ShieldCheckIcon"
        label="Quality"
        :hide-label="hideLabel"
      />
      <menu-item
        v-if="$can('list', 'Dashboard')"
        :to="{ name: 'DashboardList' }"
        icon="PresentationChartLineIcon"
        label="Dashboards"
        :hide-label="hideLabel"
      />
      <menu-item
        v-if="$can('view', 'AddressBook')"
        :to="{ name: 'AddressBookList' }"
        icon="IdentificationIcon"
        label="Address Book"
        :hide-label="hideLabel"
      />
      <menu-item
        v-if="$can('list', 'ChannelMailbox')"
        :to="{ name: 'AssignedList' }"
        icon="ChatAlt2Icon"
        label="Channel Mailbox"
        :hide-label="hideLabel"
      />
      <menu-item
        v-if="$can('list', 'Form')"
        :to="{ name: 'FormList' }"
        icon="PencilAltIcon"
        label="Forms"
        :hide-label="hideLabel"
      />
      <template
        v-for="pluginMenuItem of pluginMenuItems"
        :key="pluginMenuItem.label"
      >
        <menu-item
          v-if="
            !pluginMenuItem.permission ||
            $can(
              pluginMenuItem.permission.action,
              pluginMenuItem.permission.subject,
            )
          "
          :to="`/plugin/${pluginMenuItem.path}`"
          :icon="pluginMenuItem.icon"
          :label="pluginMenuItem.label"
          :hide-label="hideLabel"
        />
      </template>
    </div>
    <div class="pt-6 mt-6">
      <div class="px-2 space-y-1">
        <menu-item
          v-if="hasSettingsPermissions"
          :to="{ name: 'UserList' }"
          icon="CogIcon"
          label="Settings"
          :hide-label="hideLabel"
        />

        <menu-item
          v-if="$can('list', 'Campaign')"
          :to="{ name: 'CampaignList' }"
          icon="SpeakerphoneIcon"
          label="Campaigns"
          :hide-label="hideLabel"
        />
        <menu-item
          v-if="$can('list', 'Channel')"
          :to="{ name: 'ChannelList' }"
          icon="ChatAlt2Icon"
          label="Channels"
          :hide-label="hideLabel"
        />

        <menu-item
          v-if="$can('list', 'Queue')"
          :to="{ name: 'QueueList' }"
          icon="CollectionIcon"
          label="Queues"
          :hide-label="hideLabel"
        />
        <menu-item
          v-if="$can('list', 'Workflow')"
          :to="{ name: 'WorkflowList' }"
          icon="LightningBoltIcon"
          label="Workflow"
          :hide-label="hideLabel"
        />
      </div>
    </div>
  </nav>
</template>

<script setup lang="ts">
import { computed, defineComponent, onMounted, ref, watch } from 'vue'
import MenuItem from './MenuItem.vue'
import { useAuthStore } from '/@/store/authStore'
import { useLetsDialExtensions } from '/@/services/useLetsDialExtensions'
import {
  canOpenQuality,
  qualityDefaultRoute,
} from '/@/views/quality/qualityAccess'

const props = withDefaults(defineProps<{ hideLabel: boolean }>(), {
  hideLabel: false,
})

const { menuItems: pluginMenuItems } = useLetsDialExtensions()

const authStore = useAuthStore()
const hasQualityAccess = computed(() => canOpenQuality(authStore.features))
const qualityRoute = computed(() => ({
  name: qualityDefaultRoute(authStore.can.bind(authStore)),
}))
const hasSettingsPermissions = ref(false)
const checkPermissions = () => {
  hasSettingsPermissions.value =
    authStore.can('list', 'User') ||
    authStore.can('list', 'Team') ||
    authStore.can('list', 'Role') ||
    authStore.can('list', 'Extension') ||
    authStore.can('list', 'PauseCode') ||
    authStore.can('list', 'Trunk')
}
watch(
  () => authStore.permissions,
  () => checkPermissions(),
)
onMounted(() => checkPermissions())
</script>
